import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, MapPin, User, Building2, ExternalLink, Sparkles, History } from "lucide-react";
import type { WatchlistLeadTrackingRecord, TrackingHistoryEntry } from "../api/WatchlistApi";
import { getTrackingHistory } from "../api/WatchlistApi";
import { LEAD_STATUS_CHIP, LEAD_STATUS_LABEL } from "./LeadCard";
import CompanyApplyChip from "shared/CompanyApplyChip";
import RemoteScopeChip from "shared/RemoteScopeChip";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "reviewing", label: "Reviewing" },
  { value: "applied", label: "Applied" },
  { value: "interviewing", label: "Interviewing" },
  { value: "offer_received", label: "Offer Received" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Rejected" },
  { value: "not_relevant", label: "Not Relevant" },
  { value: "archived", label: "Archived" },
];

function formatHistoryDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function parseSkills(raw: string | null): string[] {
  if (!raw) return [];
  try { return JSON.parse(raw) as string[]; } catch { return [raw]; }
}

interface Props {
  record: WatchlistLeadTrackingRecord;
  isAdmin: boolean;
  onStatusChange: (trackingId: number, status: string, notes?: string) => Promise<void>;
  onClose: () => void;
}

export default function LeadDetailModal({ record, isAdmin, onStatusChange, onClose }: Props) {
  const [localStatus, setLocalStatus] = useState(record.status);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [pendingNotes, setPendingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyRect, setHistoryRect] = useState<DOMRect | null>(null);
  const [history, setHistory] = useState<TrackingHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    setHistoryLoading(true);
    getTrackingHistory(record.tracking_id, isAdmin)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [record.tracking_id, isAdmin]);

  const isPersonal = record.target_type === "personal";
  const sourceName = record.target_display_name
    || record.author_name
    || record.linkedin_url?.split("/").filter(Boolean).pop()
    || "Unknown source";
  const skills = parseSkills(record.job_skills);
  const confidencePct = record.ai_confidence != null ? Math.round(record.ai_confidence * 100) : null;

  function selectStatus(status: string) {
    if (status === localStatus) { setPendingStatus(null); setPendingNotes(""); setSaveError(null); return; }
    setPendingStatus(status);
    setPendingNotes("");
    setSaveError(null);
  }

  async function confirmStatus() {
    if (!pendingStatus) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onStatusChange(record.tracking_id, pendingStatus, pendingNotes.trim() || undefined);
      const now = new Date().toISOString();
      setHistory((prev) => [
        {
          id: Date.now(),
          tracking_id: record.tracking_id,
          from_status: localStatus,
          to_status: pendingStatus,
          notes: pendingNotes.trim() || null,
          changed_at: now,
        },
        ...prev,
      ]);
      setLocalStatus(pendingStatus);
      setPendingStatus(null);
      setPendingNotes("");
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-border shrink-0">
          <div className="w-12 h-12 rounded-xl border border-border bg-surface-muted flex items-center justify-center shrink-0">
            {isPersonal
              ? <User className="w-5 h-5 text-indigo-400" />
              : <Building2 className="w-5 h-5 text-amber-400" />
            }
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter leading-snug">
              {record.job_title || "(No job title)"}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="text-xs text-muted-foreground font-inter truncate">{sourceName}</span>
              {record.job_location && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-inter">
                  <MapPin className="w-3 h-3" />
                  {record.job_location}
                </span>
              )}
              {record.posted_at && (
                <span className="text-xs text-muted-foreground font-inter">{formatDate(record.posted_at)}</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {record.job_type && (
                <span className="text-[11px] px-2 py-1 rounded-lg border bg-muted/50 text-foreground border-border/40 font-inter">
                  {record.job_type}
                </span>
              )}
              <span className={`text-[11px] px-2 py-1 rounded-lg border font-inter ${
                isPersonal
                  ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/25"
                  : "bg-amber-500/15 text-amber-300 border-amber-500/25"
              }`}>
                {isPersonal ? "Personal" : "Company"}
              </span>
              <CompanyApplyChip companyCanApply={record.company_can_apply} metadataJson={record.company_analysis_metadata} />
              <RemoteScopeChip remoteScope={record.remote_scope} analysisJson={record.remote_worldwide_analysis} />
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors shrink-0 self-start"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* AI Analysis banner */}
        {(record.ai_reason || confidencePct != null) && (
          <div className="relative px-6 py-3 border-b border-border shrink-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-violet-500/[0.04] to-transparent pointer-events-none" />
            <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-primary/70 via-violet-400/40 to-primary/10" />
            <div className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter">
                    AI Analysis
                  </span>
                  {confidencePct != null && (
                    <span className="text-[10px] font-medium text-primary font-inter">
                      {confidencePct}% confidence
                    </span>
                  )}
                  {record.ai_provider && (
                    <span className="text-[10px] text-muted-foreground font-inter">{record.ai_provider}</span>
                  )}
                </div>
                {record.ai_reason && (
                  <p className="text-xs text-foreground font-inter leading-relaxed">{record.ai_reason}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Status tracking */}
        <div className="px-6 pt-3 pb-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter">
              Status Tracking
            </span>
            <button
              ref={historyBtnRef}
              onClick={() => {
                if (historyBtnRef.current) setHistoryRect(historyBtnRef.current.getBoundingClientRect());
                setShowHistory((v) => !v);
              }}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary font-inter transition-colors underline underline-offset-2"
            >
              <History className="w-3 h-3" />
              History
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_OPTIONS.map((opt) => {
              const isCurrent = localStatus === opt.value;
              const isPending = pendingStatus === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => selectStatus(opt.value)}
                  className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-all ${
                    isPending
                      ? `${LEAD_STATUS_CHIP[opt.value] ?? "bg-muted text-foreground border-border"} ring-2 ring-white/20 ring-offset-1 ring-offset-surface`
                      : isCurrent
                      ? LEAD_STATUS_CHIP[opt.value] ?? "bg-muted text-foreground border-border"
                      : "bg-transparent text-muted-foreground border-border hover:border-border hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {pendingStatus && (
            <div className="mt-3 space-y-2">
              <textarea
                value={pendingNotes}
                onChange={(e) => setPendingNotes(e.target.value)}
                placeholder="Add a note for this status change… (optional)"
                rows={2}
                autoFocus
                className="w-full bg-surface-muted/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground font-inter resize-none focus:outline-none focus:border-primary/60 transition-colors"
              />
              {saveError && <p className="text-[11px] text-destructive font-inter">{saveError}</p>}
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmStatus}
                  disabled={saving}
                  className="px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
                >
                  {saving ? "Saving…" : `Save as "${LEAD_STATUS_LABEL[pendingStatus] ?? pendingStatus}"`}
                </button>
                <button
                  onClick={() => { setPendingStatus(null); setPendingNotes(""); setSaveError(null); }}
                  className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-xs font-inter rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* History popover */}
        {showHistory && historyRect && createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={() => setShowHistory(false)} />
            <div
              className="fixed z-[81] w-80 bg-surface-muted border border-border rounded-xl shadow-2xl overflow-hidden"
              style={{ top: historyRect.bottom + 6, left: historyRect.left }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="text-xs font-semibold text-foreground font-inter">Tracking History</span>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="px-4 py-3 max-h-72 overflow-y-auto">
                {historyLoading ? (
                  <p className="text-xs text-muted-foreground font-inter py-1">Loading…</p>
                ) : history.length === 0 ? (
                  <p className="text-xs text-muted-foreground font-inter italic py-1">No status changes yet.</p>
                ) : (
                  <div className="space-y-3">
                    {history.map((h, i) => (
                      <div key={h.id} className="relative pl-4">
                        {i < history.length - 1 && (
                          <span className="absolute left-[3px] top-3 bottom-[-8px] w-px bg-border" />
                        )}
                        <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-surface" />
                        <div className="flex flex-wrap items-center gap-1.5">
                          {h.from_status && (
                            <>
                              <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${LEAD_STATUS_CHIP[h.from_status] ?? "text-muted-foreground border-transparent"}`}>
                                {LEAD_STATUS_LABEL[h.from_status] ?? h.from_status}
                              </span>
                              <span className="text-muted-foreground text-[10px]">→</span>
                            </>
                          )}
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${LEAD_STATUS_CHIP[h.to_status] ?? "text-muted-foreground border-transparent"}`}>
                            {LEAD_STATUS_LABEL[h.to_status] ?? h.to_status}
                          </span>
                          <span className="ml-auto text-[10px] text-muted-foreground font-inter shrink-0">
                            {formatHistoryDate(h.changed_at)}
                          </span>
                        </div>
                        {h.notes && (
                          <p className="text-[11px] text-muted-foreground font-inter mt-0.5 italic">"{h.notes}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0 space-y-5">
          {/* Skills */}
          {skills.length > 0 && (
            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-inter mb-2">
                Skills
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {skills.map((s, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-lg border bg-primary/10 text-primary border-primary/20 font-inter">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {(record.job_description || record.post_text) && (
            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-inter mb-2">
                {record.job_description ? "Job Description" : "Post"}
              </h3>
              <p className="text-sm text-foreground font-inter leading-relaxed whitespace-pre-line">
                {record.job_description || record.post_text}
              </p>
            </div>
          )}

          {!record.job_description && !record.post_text && (
            <p className="text-sm text-muted-foreground font-inter italic">No description available.</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border shrink-0 flex items-center gap-3">
          {record.application_url && (
            <a
              href={record.application_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Apply
            </a>
          )}
          <a
            href={record.post_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-border text-foreground hover:text-foreground text-sm font-inter rounded-xl transition-colors whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            View Post
          </a>
        </div>
      </div>
    </div>
  );
}
